import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Box from '@material-ui/core/Box';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import CircularProgress from '@material-ui/core/CircularProgress';
import AssignmentIcon from '@material-ui/icons/Assignment';
import axios from 'axios';
import { config } from '../../../Helpers/env';
import { formatDate as groupFormatDate } from '../../Utils/ConvertDate';
import PatientLabReport from '../../Lab/PatientLabReport';
import DoctorRadiologyResults from '../../DoctorsModule/Radiology/RadiologyResults';
import { UrgencyBadge, PathwayBadge, TimelineEvent } from '../shared';
import { surgeryService } from '../../../services/surgeryService';
import { history } from '../../../Helpers/history';


const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    overflowY: 'auto',
    padding: 20,
  },

  /* ---- Empty state ---- */
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 400,
    color: '#999',
  },
  emptyIcon: {
    fontSize: 64,
    marginBottom: 12,
    color: '#ccc',
  },

  /* ---- Patient header ---- */
  patientName: {
    fontSize: '1.35rem',
    fontWeight: 700,
    color: '#333',
    lineHeight: 1.3,
  },
  patientMeta: {
    fontSize: '0.85rem',
    color: '#666',
    marginTop: 2,
  },
  badgeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
  },

  /* ---- Section wrappers ---- */
  section: {
    backgroundColor: '#fafafa',
    borderRadius: 8,
    padding: 16,
    marginTop: 16,
  },
  sectionLabel: {
    fontSize: '0.78rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#888',
    marginBottom: 8,
  },
  sectionText: {
    fontSize: '0.93rem',
    color: '#333',
    lineHeight: 1.5,
  },
  preferredSurgeon: {
    fontSize: '0.85rem',
    color: '#555',
    marginTop: 6,
  },

  /* ---- Lab results ---- */
  labRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '6px 0',
    borderBottom: '1px solid #eee',
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  labTest: {
    fontSize: '0.85rem',
    color: '#444',
    flex: 1,
  },
  labValue: {
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#333',
    flex: 1,
    textAlign: 'center',
  },
  labChip: {
    height: 20,
    fontSize: '0.7rem',
    fontWeight: 600,
  },

  /* ---- Radiology ---- */
  radioRow: {
    marginBottom: 10,
    '&:last-child': {
      marginBottom: 0,
    },
  },
  radioStudy: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#444',
  },
  radioFinding: {
    fontSize: '0.84rem',
    color: '#555',
    marginTop: 2,
  },

  /* ---- Timeline ---- */
  timelineWrap: {
    marginTop: 8,
  },
  elapsed: {
    fontSize: '0.78rem',
    color: '#888',
    marginTop: 4,
    fontStyle: 'italic',
  },

  /* ---- Actions ---- */
  actions: {
    marginTop: 20,
    paddingTop: 16,
    borderTop: '1px solid #e0e0e0',
  },
  scheduleBtn: {
    textTransform: 'none',
    fontWeight: 600,
  },

  divider: {
    margin: '16px 0',
  },
}));

/**
 * Format a date string into a readable timestamp, e.g. "25 May 2026, 09:14"
 */
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }) + ', ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return dateStr;
  }
};

/**
 * Compute a human-readable elapsed duration between a date and now.
 */
const getElapsed = (dateStr) => {
  if (!dateStr) return '';
  try {
    const ms = Date.now() - new Date(dateStr).getTime();
    if (ms < 0) return '';
    const hours = Math.floor(ms / 3600000);
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    const mins = Math.floor(ms / 60000);
    return `${mins} minute${mins !== 1 ? 's' : ''} ago`;
  } catch {
    return '';
  }
};

const ReferralDetailPanel = ({ referral, hideActions }) => {
  const classes = useStyles();
  const [visitId, setVisitId] = useState(null);
  const [detailDiagnosis, setDetailDiagnosis] = useState(null);

  // Lab state
  const [labRequests, setLabRequests] = useState([]);
  const [labLoading, setLabLoading] = useState(false);
  const [labReportOpen, setLabReportOpen] = useState(false);
  const [labReport, setLabReport] = useState({});
  const [labTests, setLabTests] = useState([]);

  // Radiology state
  const [scanRequests, setScanRequests] = useState([]);
  const [scanLoading, setScanLoading] = useState(false);
  const [radReportOpen, setRadReportOpen] = useState(false);
  const [radiologyResults, setRadiologyResults] = useState([]);
  const [scanResults, setScanResults] = useState({});
  const [activeChip, setActiveChip] = useState(0);

  const groupLabByDate = (arr = []) => {
    const result = [];
    const knowledge = {};
    arr.forEach(item => {
      const key = groupFormatDate(item.date_created);
      if (key in knowledge) {
        result[knowledge[key]].tests.push(item);
      } else {
        knowledge[key] = result.length;
        result.push({ id: item.labrequest_id, date: key, tests: [item] });
      }
    });
    return result;
  };

  const groupScanByDate = (arr = []) => {
    const result = [];
    const knowledge = {};
    arr.forEach(item => {
      const key = groupFormatDate(item.date_created);
      if (key in knowledge) {
        result[knowledge[key]].scans.push(item);
      } else {
        knowledge[key] = result.length;
        result.push({ id: item.scan_request_id, date: key, scans: [item] });
      }
    });
    return result;
  };

  // Load diagnosis and visit_id from full referral detail
  useEffect(() => {
    setVisitId(null);
    setDetailDiagnosis(null);
    setLabRequests([]);
    setScanRequests([]);
    if (!referral) return;
    const referralId = referral.referral_id || referral.id;
    if (!referralId) return;
    surgeryService.viewReferral(referralId).then((res) => {
      const detail = res.data || {};
      const detailRef = detail.referral || {};
      if (detailRef.referral_reason) setDetailDiagnosis(detailRef.referral_reason);
      if (detailRef.visit_id) setVisitId(detailRef.visit_id);
    }).catch(() => {});
  }, [referral]);

  // Load lab and scan requests once we have visit_id
  useEffect(() => {
    if (!visitId) return;

    setLabLoading(true);
    const labForm = new FormData();
    labForm.append('visit_id', visitId);
    axios.post(`${config.smsUrl}/cdoctor/get_previous_lab_request_by_visit_id`, labForm)
      .then(res => {
        setLabRequests(groupLabByDate(Array.isArray(res.data) ? res.data : []));
      }).catch(() => {})
      .finally(() => setLabLoading(false));

    setScanLoading(true);
    const scanForm = new FormData();
    scanForm.append('visit_id', visitId);
    axios.post(`${config.smsUrl}/cdoctor/get_previous_scan_request_by_visit_id`, scanForm)
      .then(res => {
        setScanRequests(groupScanByDate(Array.isArray(res.data) ? res.data : []));
      }).catch(() => {})
      .finally(() => setScanLoading(false));
  }, [visitId]);

  const retrieveScanResult = (scan_result_id, active) => {
    const form = new FormData();
    form.append('scan_result_id', scan_result_id);
    axios.post(`${config.smsUrl}/cradiology/get_scan_results`, form)
      .then(res => {
        const data = res.data || {};
        setScanResults({ ...data, patient_info: { ...(data.patient_info || {}), visit_id: visitId } });
        setActiveChip(active);
      }).catch(() => {});
  };

  const handleOpenLabReport = () => {
    if (!visitId) return;
    const form = new FormData();
    form.append('visit_id', visitId);
    axios.post(`${config.smsUrl}/cdoctor/get_lab_results_details_by_visit_id`, form)
      .then(res => {
        const data = res.data || {};
        const rp = data.patient_info || {};
        setLabReport({ ...rp, approved_first_name: rp.approved_firstname, approved_last_name: rp.approved_lastname, visit_id: visitId });
        setLabTests((data.lab_result || []).map(item => ({ ...item, open: true })));
        setLabReportOpen(true);
      }).catch(() => {});
  };

  const handleToggleLabItem = (index) => {
    setLabTests(prev => prev.map((item, idx) => idx === index ? { ...item, open: !item.open } : item));
  };

  const handleOpenRadReport = () => {
    if (!visitId) return;
    const form = new FormData();
    form.append('visit_id', visitId);
    axios.post(`${config.smsUrl}/cdoctor/get_scan_results_details_by_visit_id`, form)
      .then(res => {
        const data = res.data || {};
        const list = data.scan_result || [];
        setRadiologyResults(list);
        if (list.length > 0) retrieveScanResult(list[0].scan_result_id, 0);
        setRadReportOpen(true);
      }).catch(() => {});
  };

  /* ---- Empty state ---- */
  if (!referral) {
    return (
      <Paper className={classes.root} elevation={0}>
        <div className={classes.emptyState}>
          <AssignmentIcon className={classes.emptyIcon} />
          <Typography variant="subtitle1" style={{ color: '#999' }}>
            Select a referral to view details
          </Typography>
        </div>
      </Paper>
    );
  }

  const {
    referral_id,
    id: referralIdAlt,
    patient_number,
    patient_name: _pname,
    patient_first_name,
    patient_last_name,
    age,
    gender,
    referring_department,
    referring_doctor,
    referring_doctor_first_name,
    referring_doctor_last_name,
    diagnosis,
    referral_reason,
    procedure_requested,
    service_name,
    urgency,
    pathway,
    status,
    created_at,
    clinical_notes,
    preferred_surgeon,
  } = referral;

  const patient_name = _pname || `${patient_first_name || ''} ${patient_last_name || ''}`.trim() || patient_number;
  const procedure_display = procedure_requested || service_name || '';
  const diagnosis_display = diagnosis || referral_reason || detailDiagnosis || '';
  const doctor_display = referring_doctor ||
    `${referring_doctor_first_name || ''} ${referring_doctor_last_name || ''}`.trim() || '';

  return (
    <Paper className={classes.root} elevation={0}>
      {/* ====== 1. Patient Header ====== */}
      <Typography className={classes.patientName}>{patient_name}</Typography>
      <Typography className={classes.patientMeta}>
        {patient_number} &nbsp;&bull;&nbsp; {age} yrs &nbsp;|&nbsp; {gender}
      </Typography>
      <div className={classes.badgeRow}>
        <UrgencyBadge urgency={urgency} />
        <PathwayBadge pathway={pathway} />
      </div>

      <Divider className={classes.divider} />

      {/* ====== 2. Diagnosis ====== */}
      <div className={classes.section}>
        <Typography className={classes.sectionLabel}>Diagnosis</Typography>
        <Typography className={classes.sectionText}>{diagnosis_display || 'N/A'}</Typography>
      </div>

      {/* ====== 3. Procedure ====== */}
      <div className={classes.section}>
        <Typography className={classes.sectionLabel}>Recommended Procedure</Typography>
        <Typography className={classes.sectionText}>{procedure_display || 'N/A'}</Typography>
        {preferred_surgeon && (
          <Typography className={classes.preferredSurgeon}>
            Preferred Surgeon: Dr.&nbsp;{preferred_surgeon}
          </Typography>
        )}
      </div>

      {/* ====== 4. Lab Results ====== */}
      <div className={classes.section}>
        <Typography className={classes.sectionLabel}>Lab Results</Typography>
        {labLoading ? (
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <CircularProgress size={14} />
            <Typography variant="body2" style={{ color: '#999', fontSize: '0.8rem' }}>Loading...</Typography>
          </Box>
        ) : labRequests.length === 0 ? (
          <Typography variant="body2" style={{ color: '#999', fontStyle: 'italic' }}>No lab results attached</Typography>
        ) : (
          labRequests.map((group, idx) => {
            const isDone = group.tests.some(t => t.item_status === 'Done');
            return (
              <div key={idx} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <Typography style={{ fontSize: '0.78rem', fontWeight: 600, color: '#555' }}>{group.date}</Typography>
                  {isDone && (
                    <Button size="small" variant="outlined"
                      style={{ fontSize: '0.7rem', padding: '2px 8px', minWidth: 'unset' }}
                      onClick={handleOpenLabReport}>
                      View Results
                    </Button>
                  )}
                </div>
                {group.tests.map((test, tidx) => (
                  <div className={classes.labRow} key={tidx}>
                    <Typography className={classes.labTest}>{test.hospital_test_name}</Typography>
                    <Chip
                      label={test.item_status || 'Pending'}
                      size="small"
                      className={classes.labChip}
                      style={{
                        backgroundColor: test.item_status === 'Done' ? '#E8F5E9' : '#FFF8E1',
                        color: test.item_status === 'Done' ? '#2E7D32' : '#F57F17',
                      }}
                    />
                  </div>
                ))}
              </div>
            );
          })
        )}
      </div>

      {/* ====== 5. Radiology Results ====== */}
      <div className={classes.section}>
        <Typography className={classes.sectionLabel}>Radiology</Typography>
        {scanLoading ? (
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <CircularProgress size={14} />
            <Typography variant="body2" style={{ color: '#999', fontSize: '0.8rem' }}>Loading...</Typography>
          </Box>
        ) : scanRequests.length === 0 ? (
          <Typography variant="body2" style={{ color: '#999', fontStyle: 'italic' }}>No radiology results attached</Typography>
        ) : (
          scanRequests.map((group, idx) => {
            const isDone = group.scans.some(s => s.item_status === 'Done');
            return (
              <div key={idx} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                  <Typography style={{ fontSize: '0.78rem', fontWeight: 600, color: '#555' }}>{group.date}</Typography>
                  {isDone && (
                    <Button size="small" variant="outlined"
                      style={{ fontSize: '0.7rem', padding: '2px 8px', minWidth: 'unset' }}
                      onClick={handleOpenRadReport}>
                      View Report
                    </Button>
                  )}
                </div>
                {group.scans.map((scan, sidx) => (
                  <div className={classes.radioRow} key={sidx}>
                    <Typography className={classes.radioStudy}>{scan.hospital_scan_name}</Typography>
                    {scan.purpose && <Typography className={classes.radioFinding}>{scan.purpose}</Typography>}
                    <Chip
                      label={scan.item_status || 'Pending'}
                      size="small"
                      className={classes.labChip}
                      style={{
                        backgroundColor: scan.item_status === 'Done' ? '#E8F5E9' : '#FFF8E1',
                        color: scan.item_status === 'Done' ? '#2E7D32' : '#F57F17',
                        marginTop: 4,
                      }}
                    />
                  </div>
                ))}
              </div>
            );
          })
        )}
      </div>

      {/* ====== 6. Referring Doctor ====== */}
      <div className={classes.section}>
        <Typography className={classes.sectionLabel}>Referring Doctor</Typography>
        <Typography className={classes.sectionText}>
          {doctor_display || 'N/A'}
        </Typography>
        {referring_department && (
          <Typography className={classes.preferredSurgeon}>
            Department: {referring_department}
          </Typography>
        )}
      </div>

      {/* ====== 7. Clinical Notes ====== */}
      <div className={classes.section}>
        <Typography className={classes.sectionLabel}>Clinical Notes</Typography>
        <Typography className={classes.sectionText}>
          {clinical_notes || 'No notes provided'}
        </Typography>
      </div>

      {/* ====== 8. Timeline ====== */}
      <div className={classes.section}>
        <Typography className={classes.sectionLabel}>Timeline</Typography>
        <div className={classes.timelineWrap}>
          <TimelineEvent
            timestamp={formatDate(created_at)}
            title="Referral Created"
            description={`Referred by Dr. ${doctor_display || 'Unknown'}`}
            color="#3F51B5"
          />
        </div>
        {created_at && (
          <Typography className={classes.elapsed}>
            {getElapsed(created_at)}
          </Typography>
        )}
      </div>

      {/* ====== 9. Action Buttons ====== */}
      {!hideActions && (
        <div className={classes.actions}>
          <button
            className="btn sms-info-btn w-100"
            onClick={() => history.push(`/surgery/schedule/${referral_id || referralIdAlt}`)}
          >
            Schedule Surgery
          </button>
        </div>
      )}

      {/* ====== Lab Results Dialog ====== */}
      <Dialog open={labReportOpen} onClose={() => setLabReportOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Lab Results</DialogTitle>
        <DialogContent dividers>
          <PatientLabReport data={labReport} labTests={labTests} handleOpenItem={handleToggleLabItem} labReport />
        </DialogContent>
        <DialogActions>
          <button className="btn btn-sm sms-info-btn" onClick={() => setLabReportOpen(false)}>Close</button>
        </DialogActions>
      </Dialog>

      {/* ====== Radiology Report Dialog ====== */}
      <Dialog open={radReportOpen} onClose={() => setRadReportOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Radiology Report</DialogTitle>
        <DialogContent dividers>
          <DoctorRadiologyResults
            radiologyResults={radiologyResults}
            retrieveResults={retrieveScanResult}
            scanResults={scanResults}
            activeChip={activeChip}
          />
        </DialogContent>
        <DialogActions>
          <button className="btn btn-sm sms-info-btn" onClick={() => setRadReportOpen(false)}>Close</button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ReferralDetailPanel;
